import { useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import {
  BillingInterval,
  CreateStripeCheckoutSessionDocument,
  SubscribeOutcome,
} from '@sandtech-hub-monorepo/ehub-client';

import { isStripeConfigured, stripePromise } from '../../utils/stripe';
import { AppRoutesPaths, subscribePath } from '../../routes/types';
import { useEnrollInProgram } from '../../data/useEnrollInProgram';
import {
  SUCCESS_ICON,
  SubscribeMessage,
  WAITING_ICON,
} from '../../components/subscribe/SubscribeMessage';

/**
 * Hosts Stripe Embedded Checkout. Stripe owns everything inside the iframe —
 * email, card, wallets, address, promo codes, the order summary, 3DS and the
 * receipt — configured entirely by the Checkout Session behind `clientSecret`.
 * Our only jobs are minting that session and rendering the states around it.
 *
 * What to buy is named in the url (`?programId=&planId=&interval=`) rather than
 * carried in router state, so a refresh or a shared link still resolves. All
 * three are only identifiers: the amount, currency and customer come from the
 * session, which is created server-side, so the url cannot influence what is
 * charged. The program is part of the key because entitlement is a join — a
 * plan unlocks a set of programs, and the server refuses a plan that does not
 * cover the one asked for.
 */
/**
 * The url carries the interval as text, so it is checked against the enum
 * before it reaches the mutation — an unrecognised value would otherwise be a
 * GraphQL validation error the learner sees as a blank failure.
 */
const parseBillingInterval = (raw: string | null) =>
  Object.values(BillingInterval).find(
    (value) => value === raw?.toUpperCase()
  ) ?? null;

export const SubscribeCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const programId = searchParams.get('programId') ?? '';
  const enrollAfterCheckout = searchParams.get('enroll') === '1';
  const planId = searchParams.get('planId') ?? '';
  const interval = parseBillingInterval(searchParams.get('interval'));

  const { enroll, enrolling } = useEnrollInProgram();

  const requested = useRef<string | null>(null);

  const [createSession, { data, error }] = useMutation(
    CreateStripeCheckoutSessionDocument,
    {
      // The page renders `error` rather than this callback. It exists to settle
      // the promise — the call is fired from an effect and nothing awaits it,
      // so without a handler Apollo rejects unhandled — and to release the
      // argument key, so a dropped connection is a failure this render rather
      // than a permanent one. Releasing it makes a retry possible; it does not
      // perform one, since the effect's deps have not changed.
      onError: () => {
        requested.current = null;
      },
    }
  );

  useEffect(() => {
    if (!programId || !planId || !interval) return;

    // Minting is not free to repeat: StrictMode's double effect would cost a
    // second round trip, and the server expires every open sibling session for
    // the plan when it mints. One request per argument triple.
    const key = `${programId}|${planId}|${interval}`;
    if (requested.current === key) return;
    requested.current = key;

    createSession({ variables: { input: { programId, planId, interval } } });
  }, [programId, planId, interval, createSession]);

  /*
   * Read straight off the mutation result rather than copying it into state in
   * `onCompleted`: Apollo clears `data` when a new call starts and drops
   * superseded responses by mutation id, but it calls `onCompleted` outside
   * that guard, so a stale response could overwrite a fresh session.
   *
   * `outcome` is the field to branch on, not `clientSecret`. Only
   * CHECKOUT_REQUIRED carries a secret: ALREADY_COVERED is a *success* with a
   * null secret, so treating null as failure would tell a learner who already
   * paid that their plan is unavailable. The payload's
   * `upgradeFromSubscriptionId` is deliberately ignored — pricing is flat, so
   * nothing outranks anything and the tier-change flow is not being built.
   */
  const session = data?.createStripeCheckoutSession;
  const outcome = session?.outcome ?? null;
  const clientSecret = session?.clientSecret ?? null;
  const checkoutSessionId = session?.checkoutSessionId ?? null;

  // Derived rather than Apollo's own `loading`, which is false between the
  // first render and the effect firing, and false again when the arguments are
  // unusable — both would flash the wrong state.
  const loading = !!programId && !!planId && !!interval && !session && !error;

  // Provider `options` are read once at init and the `key` below remounts on a
  // new secret, so a callback captured inline would hold whichever
  // checkoutSessionId existed at that moment. The ref is read at call time.
  const handleComplete = useRef<() => void>(() => undefined);

  useEffect(() => {
    handleComplete.current = () => {
      const params = new URLSearchParams({ programId });
      if (checkoutSessionId) params.set('checkoutSessionId', checkoutSessionId);
      // Forwarded untouched: the confirmation page enrols the learner in this
      // program once it has verified the session, and only when the flow began
      // with them starting it.
      if (enrollAfterCheckout) params.set('enroll', '1');
      navigate(`${AppRoutesPaths.SubscribeConfirmation}?${params}`);
    };
  }, [navigate, checkoutSessionId, programId, enrollAfterCheckout]);

  const options = useMemo(
    () => ({ clientSecret, onComplete: () => handleComplete.current() }),
    [clientSecret]
  );

  if (!isStripeConfigured || !stripePromise) {
    return (
      <SubscribeMessage title="Checkout isn't available">
        Payments aren&apos;t configured in this environment
        (`NX_STRIPE_PUBLISHABLE_KEY` is missing). If you just added it, restart
        the dev server.
      </SubscribeMessage>
    );
  }

  if (!programId || !planId || !interval) {
    return (
      <SubscribeMessage title="No plan selected">
        Choose a plan first and we&apos;ll bring you back here to pay.
      </SubscribeMessage>
    );
  }

  if (loading) {
    return (
      <SubscribeMessage
        title="Preparing your checkout"
        icon={WAITING_ICON}
        action={null}
      >
        One moment while we set this up with Stripe.
      </SubscribeMessage>
    );
  }

  // The server's refusals are written for the learner and name the actual
  // problem — a plan that doesn't cover this program, a subscription already on
  // the account. Replacing them with one generic line would lose that.
  if (error) {
    return (
      <SubscribeMessage title="We couldn't start this checkout">
        {error.message}
      </SubscribeMessage>
    );
  }

  // Before `clientSecret`, never after: ALREADY_COVERED is a success that
  // carries no secret, and checking the secret first would report it as a
  // broken plan to someone who has already paid.
  //
  // Coverage and enrolment are separate things (a subscription unlocks paid
  // modules; enrolling is what puts the program on the learner's Learning
  // page at all), so a learner can land here covered but not enrolled - not
  // the usual path (that one enrols on "Stay on Free" or once a checkout
  // completes), but reachable, e.g. a covering subscription bought for
  // another program. `enroll` is a no-op (just navigates) when already
  // enrolled, so it is safe to call unconditionally rather than trying to
  // tell the two cases apart here.
  if (outcome === SubscribeOutcome.AlreadyCovered) {
    return (
      <SubscribeMessage
        title="You already have access"
        icon={SUCCESS_ICON}
        action={
          <button
            type="button"
            disabled={enrolling}
            onClick={() => enroll(programId)}
          >
            {enrolling ? 'Enrolling…' : 'Go to my learning'}
          </button>
        }
      >
        A subscription on your account already unlocks this program, so there is
        nothing to pay for.
      </SubscribeMessage>
    );
  }

  // Reachable only once the catalogue has ranked plans in a shared family;
  // today's plans are flat, so nothing produces this. It stays an honest
  // dead-end rather than a half-built flow: the tier change is a swap on the
  // existing subscription, not a new checkout, so it cannot be served here.
  if (outcome === SubscribeOutcome.UpgradeRequired) {
    return (
      <SubscribeMessage title="This is a plan change">
        Your current subscription can be moved to this plan instead of buying a
        second one. That isn&apos;t wired up yet — contact support and
        they&apos;ll switch it for you.
      </SubscribeMessage>
    );
  }

  if (!clientSecret) {
    return (
      <SubscribeMessage title="That plan isn't available">
        The server accepted the request but returned no checkout session. Pick a
        plan from the plans page and try again.
      </SubscribeMessage>
    );
  }

  return (
    <div className="bg-background">
      {/*
        Figma 10708:57074 is a screenshot of Stripe's own checkout with the FIT
        header on top, so both columns in it — the order summary as well as the
        payment form — are rendered by Stripe inside the iframe. Nothing here
        recreates them; that would duplicate what Checkout already draws.

        All we own is the width, and it is load-bearing. Checkout picks its own
        layout from the container: **measured at 1152px** it switches from one
        column to two, stacking below that. max-w-7xl (1280px) sits above the
        threshold with headroom rather than balancing on it. Do not narrow this
        to a tidier number — under 1152px the order summary disappears into a
        single stacked column.

        Padding only widens at 2xl for the same reason: 80px gutters either side
        would starve the content below a ~1312px viewport and collapse the
        layout on a 1280px laptop.

        The design cannot be matched exactly. That node is a 1440px frame scaled
        118.39% (1704.81 / 1.1839 = 1440), putting its content at 920px — two
        380px columns with a 160px gutter. Checkout will not split at 920px, so
        the mock's own width would lose the arrangement the mock is showing. It
        is a scaled screenshot rather than a spec, so intent wins over number.
      */}
      <div className="px-4 py-10 sm:px-8 lg:py-16 2xl:px-20">
        <div className="mx-auto w-full max-w-7xl">
          <Link
            /*
             * A bare `/subscribe` here drops the scoping: `Plans` would read
             * no `programId`, treat this as a cold entry, and "Stay on Free"
             * would fall into its `navigate(-1)` branch — which, because this
             * is a `Link` (a history push, not a pop), lands one entry back
             * on THIS checkout page rather than on Plans, re-opening the
             * session. `enrollAfterCheckout` is only set for a scoped visit,
             * and only then does this page's `programId` equal the original
             * one Plans was scoped to — so it is safe to carry back.
             */
            to={
              enrollAfterCheckout ? subscribePath(programId) : subscribePath()
            }
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to plans
          </Link>
          {/*
            `key` forces a real remount when the secret changes. The provider
            initialises once and cannot swap one session for another, so without
            it a change of plan would leave the previous session mounted — a
            checkout for the plan the learner didn't pick.

            `onComplete` is what ends the flow. These sessions are minted with
            `redirect_on_completion: 'never'`, so Stripe does not navigate the
            top-level window afterwards; without this callback a paying learner
            would be left looking at a finished iframe.
          */}
          <EmbeddedCheckoutProvider
            key={clientSecret}
            stripe={stripePromise}
            options={options}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};
